import { DataGrid } from ".././Controls/DataGrid";
import { Button, Col, Container, Row } from "react-bootstrap";
import { Pencil, Trash2 } from "react-bootstrap-icons";
import { useState, useCallback, useMemo } from "react";
import { format, parseISO, isValid, parse } from "date-fns";
import { SavedReport } from "./types";
import { ColDef } from "ag-grid-community";
import { AddRecurringReportModal } from "./AddRecurringReportModal";
import { ConfirmDeleteModal } from "./DeleteRecurringReportModal";
import { PageBar } from "../Controls/PageBar";
import { gridOneCol } from "../Data/Common";
import {
  ReportsApi,
  ServerTransferRecurringReportScheduleResponse,
} from "../shared/api/axios-client";
import { createApiService } from "../shared/apiConfig";
import { toast } from "react-toastify";
import { reportTypes } from "./utils";
 
export function RecurringReports({
  savedReports,
  recurringReports,
  onReportRecurringCreated
}: {
  savedReports: SavedReport[];
  recurringReports: ServerTransferRecurringReportScheduleResponse[];
  onReportRecurringCreated?: (savedReportId: number) => void;
}) {
  const [schedules, setSchedules] =
    useState<ServerTransferRecurringReportScheduleResponse[]>(recurringReports);

  const [showModal, setShowModal] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const reportsApi = createApiService(ReportsApi);

  const filteredRecurringReports = useMemo(() => {
  return schedules.filter(r =>
    savedReports.some(s => s.id === r.savedReportId)
  );
  }, [schedules, savedReports]);

  const frequencyLabels: Record<number, string> = {
    0: "Daily",
    1: "Weekly",
    2: "Monthly",
  };

  const scheduleToEdit = editIndex !== null ? schedules[editIndex] : undefined;

  const editSchedule = useCallback((idx: number) => {
    setEditIndex(idx);
    setShowModal(true);
  }, []);

  const deleteSchedule = useCallback((idx: number) => {
    setDeleteIndex(idx);
  }, []);

  const handleDeleteSchedule = async (index: number) => {
    const schedule = schedules[index];
    if (!schedule) {
      return;
    }
    try {
      await reportsApi.monitorReportsRecurringReportDelete({
        recurringReportScheduleId: schedule.recurringReportScheduleId,
      });

      setSchedules((prev) => prev.filter((_, i) => i !== index));
      toast.success("Recurring report deleted successfully.");
    } catch (err) {
      toast.error("Failed to delete recurring report.");
    } finally {
      setDeleteIndex(null);
    }
  };

  const columns: ColDef[] = [
    {
      headerName: "Report Name",
      flex: 2,
      sortable: true,
      filter: true,
      valueGetter: (params) => {
        const savedReportId = params.data.savedReportId;
        const savedReport = savedReports.find(
          (report) => report.id === savedReportId
        );
        return savedReport!.name;
      },
    },
    {
      headerName: "Report Type",
      flex: 2,
      sortable: true,
      filter: true,
      valueGetter: (params) => {
        const savedReportId = params.data.savedReportId;
        const savedReport = savedReports.find(
          (report) => report.id === savedReportId
        );
      return reportTypes[savedReport!.type]
        ?.charAt(0).toUpperCase() + reportTypes[savedReport!.type].slice(1); // first letter capitalized
      },
    },
    {
      headerName: "Recipients",
      field: "emails",
      flex: 3,
      filter: true,
      sortable: false,
      valueFormatter: (params: any) => {
        if (!params.value || !Array.isArray(params.value)) return '';
        return params.value.join(', ');
      },
      cellRenderer: (params: any) => {
        if (!params.value || !Array.isArray(params.value)) return '';
        return (
          <>
            {params.value.map((email: string) => (
              <span key={email} className="badge bg-light text-dark me-1">
                {email}
              </span>
            ))}
          </>
        );
      },
    },
    {
      headerName: "Frequency",
      field: "frequency",
      flex: 2,
      sortable: true,
      filter: true,
      valueGetter: (params: any) => {
        const s = params.data;

        const frequencyLabel = frequencyLabels[s.frequency] ?? s.frequency;

        if (!s.time) return frequencyLabel;

        try {
          const [hoursStr, minutesStr] = s.time.split(":");
          const utcDate = new Date();
          utcDate.setUTCHours(
            parseInt(hoursStr),
            parseInt(minutesStr || "0"),
            0,
            0
          );

          const localTime = format(utcDate, "h a");
          const days = s.days?.length ? ` (${s.days.join(", ")})` : "";

          return `${frequencyLabel}${days}, ${localTime}`;
        } catch {
          return `${frequencyLabel}, -`;
        }
      },
      cellRenderer: (params: any) => {
        return params.value;
      },
    },
    {
      headerName: "Last Run",
      field: "lastRunTime",
      flex: 2,
      valueFormatter: (params: any) => {
        if (params.value && isValid(parseISO(params.value))) {
          return format(parseISO(params.value), "MM/dd/yyyy hh:mm a");
        }
        return "Never";
      },
    },
    {
      headerName: "Actions",
      flex: 1,
      cellRenderer: (params: any) => {
        const idx = params.node.rowIndex;
        return (
          <>
            <Button
              variant="link" style={{padding: "0px"}}
              size="sm"
              onClick={() => params.context.editSchedule(idx)}
            >
              <Pencil className="me-1 icon-shift-up" size={16} />
            </Button>
            <Button
              variant="link"
              size="sm"
              onClick={() => params.context.deleteSchedule(idx)}
            >
              <Trash2 className="me-1 icon-shift-up" size={16} />
            </Button>
          </>
        );
      },
      sortable: false,
      filter: false,
    },
  ];

  return (
    <Container fluid className="h-100">
     
      <Row className="h-100">
        <Col style={gridOneCol("max-content 1fr 5px")}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <PageBar title="Recurring Reports" />
            <Button onClick={() => setShowModal(true)} size="sm">
              + Add Recurring Report
            </Button>
          </div>
          <div style={{ height: "calc(100vh - 300px)", width: "100%" }}>
            <DataGrid
              rowData={filteredRecurringReports}
              columnDefs={columns}
              context={{ editSchedule, deleteSchedule }}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
            />
          </div>
          <div className="mt-2 text-muted" style={{fontSize:".9rem"}}>               
              <b>Note:</b> Recurring reports are limited to <strong>10,000 records</strong>. 
          </div>
        </Col>
      </Row>
      <AddRecurringReportModal
        show={showModal}
        savedReports={savedReports}
        schedules={filteredRecurringReports}
        initialSchedule={scheduleToEdit}
        onClose={() => {
          setShowModal(false);
          setEditIndex(null);
        }}
        onSave={(newSchedule) => {
          if (editIndex !== null) {
            setSchedules((prev) =>
              prev.map((s, i) => (i === editIndex ? newSchedule : s))
            );
          } else {
            setSchedules((prev) => [...prev, newSchedule]);
            onReportRecurringCreated?.(newSchedule.savedReportId!);
          }
          setShowModal(false);
          setEditIndex(null);
        }}
      />

      <ConfirmDeleteModal
        show={deleteIndex !== null}
        onCancel={() => setDeleteIndex(null)}
        onConfirm={async () => {
          if (deleteIndex !== null) {
            await handleDeleteSchedule(deleteIndex);
          }
        }}
      />
    </Container>
  );
}
